/**
 * ProcessLoader Component - Loading Spinner with Message Display
 * 
 * Displays a centered loading spinner with an optional message
 * Designed for in-process operations with custom messaging
 * Initially hidden with d-none class, shown/hidden via JavaScript manipulation
 * 
 * Features:
 * - Centered spinner and message using Bootstrap flexbox
 * - Custom message display via pmMessage prop
 * - Customizable spinner size via CSS class
 * - Hidden by default (d-none class) - shown programmatically
 * - Uses unique ID for DOM targeting and visibility control
 * - Primary color spinner matching Bootstrap theme
 * 
 * Visibility Control:
 * - Initially hidden with 'd-none' class
 * - Shows/hides via JavaScript DOM manipulation
 * - Requires JavaScript to toggle display
 * - Example: document.getElementById('process-loader').classList.remove('d-none')
 * 
 * Styling:
 * - Container: Flexbox centered both horizontally and vertically
 * - Spinner: Bootstrap spinner-border with custom size class
 * - Message: Custom text displayed next to spinner
 * - d-none: Bootstrap display-none utility class
 * 
 * Dependencies:
 * - React: UI library
 * - Bootstrap CSS: Flexbox classes and spinner component
 * - Custom CSS: process-loader-size and process-loader-message classes
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.pmMessage=""] - Message text to display next to spinner
 * 
 * @returns {JSX.Element} Hidden loader container that can be shown programmatically
 * 
 * @example
 * // Component usage
 * <ProcessLoader pmMessage="Processing your request..." />
 * 
 * @example
 * // Show loader from another component
 * const showLoader = () => {
 *   document.getElementById('process-loader').classList.remove('d-none');
 * };
 * 
 * @example
 * // Hide loader when done
 * const hideLoader = () => {
 *   document.getElementById('process-loader').classList.add('d-none');
 * };
 * 
 * @example
 * // During API call
 * const handleSubmit = async () => {
 *   showLoader();
 *   try {
 *     const result = await apiCall();
 *     // Handle result
 *   } finally {
 *     hideLoader();
 *   }
 * };
 */

import React from 'react';

/**
 * ProcessLoader Component
 * Displays a loading spinner with accompanying message
 * Initially hidden, shown/hidden via JavaScript DOM manipulation
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.pmMessage=""] - Custom message to display during processing
 * @returns {JSX.Element} Hidden loader with spinner and message
 */
const ProcessLoader = ({ pmMessage }) => {
  return (
    <div 
      className="d-flex justify-content-center align-items-center d-none" 
      id="process-loader"
    >
      {/* 
        Loading spinner
        - spinner-border: Bootstrap animated spinner component
        - text-primary: Primary theme color
        - process-loader-size: Custom CSS class for spinner sizing
        - role="status": Indicates loading/status element
        - &nbsp;: Empty content (icon content)
      */}
      <div className="spinner-border text-primary process-loader-size" role="status">&nbsp;</div>
      
      {/* 
        Loading message
        - process-loader-message: Custom CSS class for styling message text
        - Displays dynamic message passed via pmMessage prop
      */}
      <span className="process-loader-message">{pmMessage}</span>
    </div>
  );
};

export default ProcessLoader;
